import hyperdom from 'hyperdom'
import Search from './search.jsx'
import * as roomsKey from '../../../../packages/accommodation/roomsKey.js'
import formatPrice from '../packages/ui-helpers/formatPrice.js'
import formatRelativePrice from '../packages/ui-helpers/formatRelativePrice.js'
import Transfers from './transfers.jsx'
import RoomOptions from '../roomOptions.jsx'
import baggageNotes from '../flights/baggageNotes.jsx'
import * as gtmDataLayer from '../analytics/gtm/dataLayer.js'
import {Message, MessageTypes} from '../message.jsx'
import {calculateLegsForItinerary} from './tropicsLeg.js'
import {calculateAirfareParty} from 'root-packages/calculateAirfareParty.js'

export default class Flights {
  constructor (options) {
    var self = this
    this.config = options.config
    this.quoteCriteria = options.quoteCriteria
    this.promotions = options.promotions
    options.quoteCriteria.flights = this
    this.transfers = new Transfers(options)
    this.api = options.api
    this.loadAirPromotions = options.loadAirPromotions
    this.dmpg = options.dmpg
    this.user = options.user
    this.transfersEnabled = options.config.features.transfers.enabled

    this.requestData = {
      type: 'explore',
      callback: true,
      departTime: 'Any',
      returnTime: 'Any',
      airline: 'any',
      getDepartDate (date, time, defaultTime) {
        date = date || this.departureDate
        time = time || this.departTime
        if (!date) return 'Invalid Date'

        if (time === 'Any' || time === undefined) {
          time = defaultTime || options.config.flights.defaultDepartTime
        }

        return `${date}T${time}`
      },
      getReturnDate (date, time, defaultTime) {
        date = date || this.returnDate
        time = time || this.returnTime
        if (!date) return 'Invalid Date'

        if (time === 'Any' || time === undefined) {
          time = defaultTime || options.config.flights.defaultReturnTime
        }
        return `${date}T${time}`
      },

      isValid (){
        return (!!this.departureAirport &&
          this.getDepartDate() !== 'Invalid Date' &&
          this.getReturnDate() !== 'Invalid Date')
          || (this.multiCity && this.multiCity.length > 0)
      },
    }

    this.showLoading = options.showLoading
    this.hideLoading = options.hideLoading
    this.modals = options.modals
    this.disableLoadingTimeout = options.disableLoadingTimeout
    this.exploreFlights = this.requestData
    this.departure = options.departure
    this.isFlightsOpen = false

    this.flights = {
      type: 'own',
      searchMode: 'loading',
      defaultDepartDate: this.departure.startDate,
      defaultReturnDate: this.departure.endDate,
      allowedActions: {
        IsSearchAllowed: true,
        IsMultiStopAllowed: true,
      },
      defaultAirports: {
        start: options.departure.startPlace.airport,
        end: options.departure.endPlace.airport,
      },
      select (trip) {
        return self.select(trip)
      },
      remove () {
        delete self.quoteCriteria.promotionInvalidDueToAutomaticPromotion
        delete self.requestData.id
        delete this.selected
        delete self.selected
        this.clearWarnings()
        self.transfers.remove()
        this.searchMode = 'criteria'
      },
      clearWarnings () {
        delete self.showFlightsRemovedForPartyWarning
        delete self.showFlightsRemovedForExtraNightsWarning
      },
    }

    this.search = new Search(this)

    this.roomOptions = new RoomOptions({
      binding: {
        get: () => options.quoteCriteria.selectedRoomAllocation,
        set: (rooms) => options.quoteCriteria.selectRoomAllocation(rooms),
      },
      config: this.config,
      quoteCriteria: options.quoteCriteria,
    })
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  select (trip, selectedTransfers) {
    return this.loadData().then(() => {
      if (!trip.legs) {
        trip.legs = calculateLegsForItinerary(trip.itinerary)
      }
      this.requestData.id = trip.id
      this.selected = trip
      this.flights.selected = trip

      this.showLoading()
      return Promise.all([
        this.transfersEnabled ? this.transfers.load(trip).then(() => this.transfers.selectById(selectedTransfers)) : undefined,
        this.loadTropicsAirPromos(this.quoteCriteria, this.user),
      ]).then(responses => {
        this.hideLoading()
        const promo = responses[1][0]
        if(promo) {
          this.promotions.addPromotion(promo)
        }
      })
    }).catch(ex => {
      this.hideLoading()
      console.error(ex)
    })
  }

  loadTropicsAirPromos (quoteCriteria, user) {
    return this.api.airPromotions(
      {
        departureCode: quoteCriteria.departure.code,
        tourCode: quoteCriteria.departure.tourCode,
        sellingCompanyCode: quoteCriteria.departure.sellingCompanyCode,
        user,
        flights: quoteCriteria.flights.selected,
      }
    )
  }

  loadData () {
    if (!this._loadingData) {
      this.airports = []
      var homeCountry = this.config.country
      return this._loadingData = Promise.all([
        this.api.airports(this.config.sellingCompanyCode),
        this.loadAirPromotions(),
      ]).then(([airports, airPromotions]) => {
        this.airPromotions = airPromotions
        function formatAirport (airport) {
          return `${airport.name} ${airport.code}`
        }
        this.airports = airports
        const departureStartAirport = this.airports.find(airport => airport.code === this.departure.startPlace.airport)
        this.arriveAirportName = departureStartAirport && formatAirport(departureStartAirport) || this.departure.startPlace.city
        this.allAirports = airports.map(formatAirport)
        this.homeCountryAirports = airports
          .filter(airport => { return airport.country === homeCountry || this.config.flights.nonOriginCountryAllowed })
          .map(formatAirport)

        if (this.flights.searchMode === 'loading') {
          this.flights.searchMode = 'criteria'
        }
        this.dataLoaded = true
      }, (error) => {
        this.dataLoaded = true
        this.hideLoading()
        throw error
      })
    } else {
      return this._loadingData
    }
  }

  rooms (){
    return roomsKey.stringify(this.quoteCriteria.selectedRoomAllocation.rooms)
  }

  party () {
    const getDateFromSelectedFlights = () => this.flights.selected.legs.slice(-1)[0].lastFlight.departDate
    const getDateFromSearchParams = () => this.exploreFlights.returnDate || this.exploreFlights.multiCity[this.exploreFlights.multiCity.length-1].departureDate
    const date = this.flights.selected ? getDateFromSelectedFlights() : getDateFromSearchParams()
    return calculateAirfareParty(this.quoteCriteria.passengers, date)
  }

  formatPrice (price) {
    return formatPrice({
      currency: this.departure.prices.currency,
      amount: price,
    })
  }

  formatRelativePrice (price){
    return formatRelativePrice({
      currency: this.departure.prices.currency,
      amount: price,
      noPrefixSymbol: true,
    })
  }

  clear () {
    this.selected = this.flights.selected = null
    this.flights.searchMode = 'criteria'
    this.transfers.remove()
  }

  renderFlightsRemovedForPartyWarning () {
    if (this.showFlightsRemovedForPartyWarning) {
      return new Message({
        className: 'flights-removed-warning',
        header: 'Flights removed',
        message: 'As you have added or removed people from your party you will need to search for flights again.',
        type: MessageTypes.WARNING,
        onClose: () => this.hideFlightsRemovedForPartyWarning(),
      })
    }
  }

  hideFlightsRemovedForPartyWarning () {
    this.showFlightsRemovedForPartyWarning = false
  }

  renderFlightsRemovedForExtraNightsWarning () {
    if (this.showFlightsRemovedForExtraNightsWarning) {
      return new Message({
        className: 'flights-removed-warning',
        header: 'Flights removed',
        message: `As you have changed the number of nights you will be staying in ${this.departure.startPlace.city} either side of the tour you will need to search for flights again.`,
        type: MessageTypes.WARNING,
      })
    }
  }

  toggleFlights () {
    if (this._loadingData) {
      this.showLoading()
      this.flights.type = 'explore'

      this.isFlightsOpen = !this.isFlightsOpen

      return this.loadData().then(() => {
        this.hideLoading()
      })
    }
  }

  toggleButtonText () {
    return this.isFlightsOpen
      ? 'Hide Flights'
      : this.flights.selected
        ? 'Show Flights'
        : 'Add Flights'
  }

  renderFlights () {
    if (this.selected) {
      this.flights.type = 'explore'
      this.flights.searchMode = 'selected'
    }
    return (
      <div class="flights">
        <div className='flights__header'>
          <h2>Flights to connect you with your trip</h2>
          <span>Optional</span>
        </div>
        {this.renderCommonFlightsContainer()}
        <div className={["flights__search-container",{'show-expanded': this.isFlightsOpen}]}>
          <div className={[{'flights__search-inner': this.flights.searchMode !== 'criteria'}]}>
            {this.search}
          </div>
          <div className="flights__search-info-small u-margin-top-small">
            For your benefit and clarity, the total airfare shown includes airline imposed fuel surcharges and
            government taxes and fees. If you need more clarification please see our <a
              href={this.config.brandPages.termsAndConditions} target="_blank">terms & conditions</a>.
            {baggageNotes(this)}
          </div>
        </div>
      </div>
    )
  }

  renderCommonFlightsContainer () {
    return <div class="flights__toggle-container">
      <div  class="flights__toggle-icon-with-text">
        <i class='flights__company-info-icon -new-ui_plane'></i>
        <div class="flights__toggle-text">Search for the best possible flights for you</div>
      </div>
      <button
          class={['btn-primary--reversed-colors', 'flights__toggle-button']}
          type="button" onclick={() => this.toggleFlights()}
          data-item-type='General Interaction'
          data-item-name={`${this.toggleButtonText().split(' ').join('-').toLowerCase()}-cta`}
      >
        {this.toggleButtonText()}
      </button>
    </div>
  }

  renderTransfers () {
    return this.transfers
  }

  render () {
    return <div>
      {this.renderFlights()}
      {this.renderTransfers()}
    </div>
  }
}

